import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Check if we have valid Supabase credentials (no logging for security)
const hasValidSupabaseConfig = supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl !== 'your_supabase_url_here' && 
  supabaseAnonKey !== 'your_supabase_anon_key_here' &&
  supabaseUrl.startsWith('https://');

if (!hasValidSupabaseConfig) {
  console.warn('Supabase environment variables not properly configured. Authentication features will not work.');
}

// Create a mock client for development when Supabase is not configured
const createMockSupabaseClient = () => ({
  auth: {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    signUp: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    signInWithPassword: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    signOut: () => Promise.resolve({ error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    resetPasswordForEmail: () => Promise.resolve({ error: { message: 'Supabase not configured' } }),
    updateUser: () => Promise.resolve({ error: { message: 'Supabase not configured' } }),
    getUser: () => Promise.resolve({ data: { user: null }, error: null })
  }
});

export const supabase = hasValidSupabaseConfig 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : createMockSupabaseClient();

// Auth helper functions
export const authHelpers = {
  // Sign up user
  signUp: async (email, password, fullName, grade, parentEmail) => {
    try {
      if (!hasValidSupabaseConfig) {
        return { success: false, error: 'Authentication not configured. Please set up Supabase credentials.' };
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            grade: grade,
            parent_email: parentEmail
          }
        }
      });
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sign in user
  signIn: async (email, password) => {
    console.log('🔍 signIn called with:', { email, hasValidSupabaseConfig });
    try {
      if (!hasValidSupabaseConfig) {
        console.error('🔍 Supabase not configured properly');
        return { success: false, error: 'Authentication not configured. Please set up Supabase credentials.' };
      }
      
      console.log('🔍 Attempting Supabase signInWithPassword...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('🔍 Supabase signIn error:', error);
        throw error;
      }
      console.log('🔍 Supabase signIn success:', data);
      return { success: true, data };
    } catch (error) {
      console.error('🔍 signIn catch error:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign out user
  signOut: async () => {
    try {
      if (!hasValidSupabaseConfig) {
        return { success: true }; // Mock success for demo
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      if (!hasValidSupabaseConfig) {
        return { success: true, user: null };
      }
      
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      if (!hasValidSupabaseConfig) {
        return { success: false, error: 'Authentication not configured. Please set up Supabase credentials.' };
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.mathscampus.com',
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update password
  updatePassword: async (newPassword) => {
    try {
      if (!hasValidSupabaseConfig) {
        return { success: false, error: 'Authentication not configured. Please set up Supabase credentials.' };
      }
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Create or update user profile in our database
  updateUserProfile: async (userData) => {
    try {
      if (!hasValidSupabaseConfig) {
        // Return mock success for demo mode
        return { 
          success: true, 
          user: {
            id: userData.user_id,
            email: userData.email,
            full_name: userData.full_name,
            grade: userData.grade, // Include grade in mock response
            parent_email: userData.parent_email, // Include parent_email in mock response
            role: 'student',
            subscription_tier: 'free'
          }
        };
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Check test eligibility
  checkTestEligibility: async (userId) => {
    try {
      if (!hasValidSupabaseConfig) {
        // Return default eligibility for demo mode
        return { 
          can_take_test: true, 
          tests_taken: 0, 
          remaining_tests: 3, 
          role: 'student',
          subscription_tier: 'free',
          message: 'Demo mode: You have 3 free tests available'
        };
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/test/eligibility/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error checking test eligibility:', error);
      return { 
        can_take_test: true, 
        tests_taken: 0, 
        remaining_tests: 3, 
        role: 'student',
        subscription_tier: 'free',
        message: 'Welcome! You have 3 free tests available'
      };
    }
  }
};

export default supabase;